import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createRecord, updateRecord } from '../../services/recordStorageService';
import { TabbedModal } from './TabbedModal';
import { usePageLayout } from '../../hooks/useObjectManager';
import { EditSection } from '../edit/EditSection';
import { useToast } from '../../contexts/ToastContext';
import { ListViewRecord } from '../../types/listView';
import { CheckSquare, Link, List, FileText, History } from 'lucide-react';
import { validateRecord, type ValidationError } from '../../services/queries/validationRuleQueries';
import { ValidationErrors } from '../common/ValidationErrors';
import { RelatedRecordsTab } from '../tabs/RelatedRecordsTab';
import { SubtasksTab } from '../tabs/SubtasksTab';
import { ActivityTab } from '../tabs/ActivityTab';

interface TaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task?: ListViewRecord | null;
}

export const TaskModal: React.FC<TaskModalProps> = ({
  isOpen,
  onClose,
  task
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();
  const isEditMode = !!task;

  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Task__c', task);

  const [formData, setFormData] = useState<Record<string, any>>({});

  const [notes, setNotes] = useState<string[]>([]);
  const [newNote, setNewNote] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form from record properties directly
  useEffect(() => {
    if (task) {
      const data: Record<string, any> = {};
      for (const [key, value] of Object.entries(task)) {
        if (value !== undefined && value !== null && typeof value !== 'object') {
          data[key] = value;
        }
      }
      setFormData(data);
      setNotes(task.notes as string[] || []);
    } else {
      setFormData({});
      setNotes([]);
    }
    setErrors({});
    setNewNote('');
  }, [task, isOpen]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
    if (errors[fieldName]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  const handleAddNote = () => {
    if (newNote.trim()) {
      setNotes([...notes, newNote.trim()]);
      setNewNote('');
    }
  };

  const handleRemoveNote = (index: number) => {
    setNotes(notes.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const subject = String(formData.subject || formData.Subject__c || '').trim();
    if (!subject) {
      newErrors.Subject__c = 'Subject is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, any> & { notes: string[] }) => {
      const { notes: _notes, ...fields } = data;
      const gobodoData: Record<string, any> = {};
      for (const [key, value] of Object.entries(fields)) {
        if (value !== undefined && value !== null && value !== '' && key !== 'id' && key !== 'label') {
          gobodoData[key] = value;
        }
      }

      if (isEditMode && task) {
        const updated = await updateRecord('Task__c', String(task.id), gobodoData);
        if (!updated) throw new Error('Failed to update task');
        return { ...task, ...updated };
      } else {
        const created = await createRecord('Task__c', gobodoData);
        return { ...data, id: created.id };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taskListViews'] });
      queryClient.invalidateQueries({ queryKey: ['taskRecords'] });
      queryClient.invalidateQueries({ queryKey: ['dashboardMetrics'] });
      queryClient.invalidateQueries({ queryKey: ['taskListViewData'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Task__c'] });
      queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Task'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['task', task?.id] });
      }

      const taskTitle = (data as any).Title__c || 'Task';
      success(
        isEditMode ? 'Task updated' : 'Task created',
        isEditMode ? `${taskTitle} has been updated` : `${taskTitle} has been created`
      );
      onClose();
    },
    onError: (error: any) => {
      console.error('[TaskModal] Save error:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to save task. Please try again.';
      showError('Error', typeof msg === 'string' ? msg : 'Failed to save task. Please try again.');
    }
  });

  const handleSubmit = (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    if (!validate()) {
      showError('Validation Error', 'Please fix the errors before submitting');
      return;
    }
    saveMutation.mutate({ ...formData, notes });
  };

  // Conditional tabs based on mode
  const tabs = isEditMode
    ? [
        { id: 'overview', label: 'Overview', icon: CheckSquare },
        { id: 'related', label: 'Related To', icon: Link },
        { id: 'subtasks', label: 'Subtasks', icon: List },
        { id: 'notes', label: 'Notes', icon: FileText },
        { id: 'history', label: 'History', icon: History }
      ]
    : [
        { id: 'overview', label: 'Overview', icon: CheckSquare }
      ];

  return (
    <TabbedModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Task' : 'New Task'}
      tabs={tabs}
      maxWidth="2xl"
      actions={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#334155] rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveMutation.isPending
              ? 'Saving...'
              : isEditMode ? 'Save Changes' : 'Create Task'
            }
          </button>
        </div>
      }
    >
      {(activeTab) => (
        <form onSubmit={handleSubmit} className="space-y-6">
          {activeTab === 'overview' && (
            <div className="max-h-[60vh] overflow-y-auto pr-2">
              {loadingLayout ? (
                <div className="space-y-6">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map(i => (
                      <div key={i}>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2"></div>
                        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pageLayout?.sections && pageLayout.sections.length > 0 ? (
                (() => {
                  // Ensure Subject__c is in the layout — gobodo requires it
                  const allFields = pageLayout.sections.flatMap(s => s.fields || []);
                  const hasSubject = allFields.some(f =>
                    f === 'Subject__c' || f === 'subject' || (typeof f === 'string' && f.toLowerCase() === 'subject__c')
                  );
                  const sections = hasSubject
                    ? pageLayout.sections
                    : [
                        { ...pageLayout.sections[0], fields: ['Subject__c', ...(pageLayout.sections[0].fields || [])] },
                        ...pageLayout.sections.slice(1)
                      ];
                  return sections.map(section => (
                    <EditSection
                      key={section.id}
                      section={section}
                      objectApiName="Task__c"
                      formData={formData}
                      onChange={handleFieldChange}
                      errors={errors}
                      disabled={saveMutation.isPending}
                    />
                  ));
                })()
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <p>No page layout configured for Task.</p>
                  <p className="text-sm mt-2">Go to Object Manager to configure page layouts.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'related' && task?.id && (
            <RelatedRecordsTab
              recordId={task.id}
              objectType="Task__c"
              recordName={task.title as string}
            />
          )}

          {activeTab === 'subtasks' && task?.id && (
            <SubtasksTab
              taskId={task.id}
              taskTitle={task.title as string}
            />
          )}

          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Add Note
                </label>
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  placeholder="Type your note here..."
                  rows={4}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddNote}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Add Note
                </button>
              </div>

              {notes.length > 0 ? (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notes ({notes.length})
                  </label>
                  <div className="space-y-2">
                    {notes.map((note, index) => (
                      <div
                        key={index}
                        className="p-3 bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 rounded-lg"
                      >
                        <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{note}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveNote(index)}
                          className="text-sm text-red-600 dark:text-red-400 hover:underline mt-2"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    No notes yet. Add your first note above.
                  </p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'history' && task?.id && (
            <ActivityTab
              recordId={task.id}
              objectType="Task__c"
              recordName={task.title as string}
            />
          )}
        </form>
      )}
    </TabbedModal>
  );
};
